import { Avatar, Box, BoxProps, Flex, Heading, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import authApi from '~/authApi';
import IconButton from '~/components/IconButton';
import { EditIcon } from '~/components/icons';
import { FEATURE_FLAGS, USER_ROLES } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { DefaultMediaPackType } from '~/types';
import { convertPxToRems } from '~/utils-ts';

interface Props extends BoxProps {
  avatarUrl: string;
  mediaPack: DefaultMediaPackType;
  onEdit?: () => void;
  isSharedView?: boolean;
}

export const AboutYou: React.FC<Props> = ({
  mediaPack = {} as DefaultMediaPackType,
  avatarUrl,
  onEdit = () => {},
  isSharedView,
  ...props
}) => {
  const { isEnabled: isMediaPacksDeprecated } = useFeature(
    FEATURE_FLAGS.foamDeprecateMediaPacks,
  );
  const { isEnabled: isMediaKitsForCreatorsEnabled } = useFeature(
    FEATURE_FLAGS.foamCreatorMediaKits,
  );

  const canEditMediaPack = useMemo(() => {
    const userIsManager = authApi.user.roles.includes(USER_ROLES.talentManager);

    if (isSharedView) return false;

    if (userIsManager) {
      return !isMediaPacksDeprecated;
    }

    return !isMediaKitsForCreatorsEnabled;
  }, [isMediaPacksDeprecated, isMediaKitsForCreatorsEnabled, isSharedView]);

  return (
    <Box as="section" {...props}>
      <Flex
        alignItems={{ md: 'center' }}
        flexDirection={{ base: 'column', sm: 'row' }}
        justifyContent="space-between"
        flexWrap="nowrap"
      >
        <Flex direction="row" flexWrap="nowrap" alignItems="center" gap={4}>
          <Avatar src={avatarUrl} boxSize={convertPxToRems(72)} />
          <Heading
            as="h1"
            fontSize={{ base: '4xl', sm: '6xl' }}
            noOfLines={1}
            fontWeight={500}
          >
            {mediaPack?.aboutMeName}
          </Heading>
        </Flex>
        {canEditMediaPack && (
          <IconButton
            borderRadius="full"
            boxSize={10}
            _hover={{
              backgroundColor: 'primary.50',
            }}
            variant="ghost"
            aria-label="Edit about you"
            icon={<EditIcon onClick={onEdit} />}
          />
        )}
      </Flex>

      <Text
        marginY={{ base: 5, md: 6 }}
        maxWidth={{ base: 'auto', lg: '450px' }}
        lineHeight="shorter"
        letterSpacing="wide"
        data-testid="about-you-description"
      >
        {mediaPack.aboutMeDescription ||
          'My goal is to inspire, educate and entertain. I partner with brands I believe in, and find unique and interesting ways to integrate their story into my content. Smash that Contact Button. Let’s do something great together.'}
      </Text>
    </Box>
  );
};
